import { useState, useEffect } from "react";
import { 
  Plus, 
  ArrowUp, 
  ArrowDown, 
  DollarSign, 
  Calendar, 
  Filter,
  FileText,
  BarChart4,
  PieChart,
  Search,
  Clock,
  CheckCircle2,
  AlertCircle,
  CalendarClock,
  Users as UsersIcon
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/lib/toast";
import { format, parseISO, isAfter, isBefore, startOfMonth, endOfMonth, subDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import { TransactionDialog } from "./components/TransactionDialog";
import { TransactionList } from "./components/TransactionList";
import { FinanceChart } from "./components/FinanceChart";
import { RecurringTransactionDialog } from "./components/RecurringTransactionDialog";
import { RecurringTransactionList } from "./components/RecurringTransactionList";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AdminAccess } from "@/components/auth/AdminAccess";
import { FinancialTransaction, Client, RecurringTransaction } from "@/types/database";

interface Transaction {
  id: string;
  description: string;
  amount: number;
  category: string;
  date: Date;
  type: "income" | "expense";
  status: "paid" | "pending" | "overdue";
  clientName?: string;
  client_id?: string | null;
}

interface FinancialSummary {
  totalIncome: number;
  totalExpense: number;
  balance: number;
  overdueAmount: number;
  pendingAmount: number;
  paidThisMonth: number;
  averageIncome30Days: number;
  clientsWithOverduePayments: number;
}

export default function Finances() {
  const [filter, setFilter] = useState("all");
  const [period, setPeriod] = useState("month");
  const [searchQuery, setSearchQuery] = useState("");
  const [view, setView] = useState<"kanban" | "list">("kanban");
  const queryClient = useQueryClient();
  
  // Trigger the function to generate upcoming transactions from recurring patterns
  const generateTransactions = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: recurringData, error: recurringError } = await supabase
        .from('recurring_transactions')
        .select('*')
        .eq('active', true)
        .eq('user_id', user.id);
        
      if (recurringError) throw recurringError;
      
      const todayDate = new Date();
      const currentDay = todayDate.getDate();
      const currentMonth = todayDate.getMonth() + 1;
      const currentYear = todayDate.getFullYear();
      
      for (const rec of recurringData || []) {
        let dueDate: Date;
        
        if (currentDay <= rec.day_of_month) {
          dueDate = new Date(currentYear, currentMonth - 1, rec.day_of_month);
        } else {
          if (currentMonth === 12) {
            dueDate = new Date(currentYear + 1, 0, rec.day_of_month);
          } else {
            dueDate = new Date(currentYear, currentMonth, rec.day_of_month);
          }
        }
        
        const formattedDueDate = format(dueDate, 'yyyy-MM-dd');
        
        const { data: existingTx, error: checkError } = await supabase
          .from('financial_transactions')
          .select('id')
          .eq('client_id', rec.client_id)
          .eq('description', rec.description)
          .eq('transaction_date', formattedDueDate)
          .eq('transaction_type', rec.transaction_type)
          .eq('user_id', user.id);
          
        if (checkError) throw checkError;
        
        if (!existingTx || existingTx.length === 0) {
          const { error: insertError } = await supabase
            .from('financial_transactions')
            .insert({
              client_id: rec.client_id,
              description: rec.description,
              amount: rec.amount,
              category: rec.category,
              transaction_type: rec.transaction_type,
              transaction_date: formattedDueDate,
              paid: false,
              status: 'pending',
              user_id: user.id
            });
            
          if (insertError) throw insertError;
          
          const { error: updateError } = await supabase
            .from('recurring_transactions')
            .update({ last_generated_date: formattedDueDate })
            .eq('id', rec.id);
            
          if (updateError) throw updateError;
        }
      }
      
      const { error: overdueError } = await supabase
        .from('financial_transactions')
        .update({ status: 'overdue' })
        .eq('paid', false)
        .eq('user_id', user.id)
        .lt('transaction_date', format(todayDate, 'yyyy-MM-dd'));
        
      if (overdueError) throw overdueError;
      
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['recurring-transactions'] });
      queryClient.invalidateQueries({ queryKey: ['financial-summary'] });
    } catch (error: any) {
      console.error("Error generating transactions:", error);
    }
  };

  useEffect(() => {
    generateTransactions();
    
    const interval = setInterval(generateTransactions, 3600000);
    return () => clearInterval(interval);
  }, []);
  
  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('clients')
        .select('id, name')
        .eq('user_id', user.id)
        .order('name');
      
      if (error) {
        toast.error('Erro ao carregar clientes: ' + error.message);
        return [];
      }
      
      return data as Client[];
    }
  });
  
  const { data: transactions = [], isLoading: isLoadingTransactions } = useQuery({
    queryKey: ['transactions'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('financial_transactions')
        .select('*, clients(name)')
        .eq('user_id', user.id)
        .order('transaction_date', { ascending: false });
      
      if (error) {
        toast.error('Erro ao carregar transações: ' + error.message);
        return [];
      }
      
      return data.map(transaction => ({
        id: transaction.id,
        description: transaction.description,
        amount: Number(transaction.amount),
        category: transaction.category || 'Outros',
        date: new Date(transaction.transaction_date),
        type: transaction.transaction_type as "income" | "expense",
        status: transaction.status || (transaction.paid ? "paid" : 
                (new Date(transaction.transaction_date) < new Date() ? "overdue" : "pending")),
        clientName: transaction.clients?.name,
        client_id: transaction.client_id
      })) as Transaction[];
    }
  });
  
  const { data: recurringTransactions = [], isLoading: isLoadingRecurring } = useQuery({
    queryKey: ['recurring-transactions'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('recurring_transactions')
        .select('*, clients(name)')
        .eq('active', true)
        .eq('user_id', user.id);
      
      if (error) {
        toast.error('Erro ao carregar cobranças recorrentes: ' + error.message);
        return [];
      }
      
      const result = data.map(rt => {
        const today = new Date();
        const currentDay = today.getDate();
        const currentMonth = today.getMonth();
        const currentYear = today.getFullYear();
        
        let nextDueDate: Date;
        
        if (currentDay <= rt.day_of_month) {
          nextDueDate = new Date(currentYear, currentMonth, rt.day_of_month);
        } else {
          if (currentMonth === 11) {
            nextDueDate = new Date(currentYear + 1, 0, rt.day_of_month);
          } else {
            nextDueDate = new Date(currentYear, currentMonth + 1, rt.day_of_month);
          }
        }
        
        const daysUntilDue = Math.ceil((nextDueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        
        return {
          ...rt,
          id: rt.id,
          client_id: rt.client_id,
          client_name: rt.clients.name,
          description: rt.description,
          amount: Number(rt.amount),
          day_of_month: rt.day_of_month,
          category: rt.category || 'Geral',
          transaction_type: rt.transaction_type,
          active: rt.active,
          created_at: rt.created_at,
          user_id: rt.user_id,
          next_due_date: format(nextDueDate, 'yyyy-MM-dd'),
          days_until_due: daysUntilDue
        } as RecurringTransaction;
      });
      
      return result;
    }
  });
  
  const { data: financialSummary = {
    totalIncome: 0,
    totalExpense: 0,
    balance: 0,
    overdueAmount: 0,
    pendingAmount: 0,
    paidThisMonth: 0,
    averageIncome30Days: 0,
    clientsWithOverduePayments: 0
  }, isLoading: isLoadingSummary } = useQuery({
    queryKey: ['financial-summary'],
    queryFn: async () => {
      const now = new Date();
      const thisMonthStart = startOfMonth(now);
      const thisMonthEnd = endOfMonth(now);
      const thirtyDaysAgo = subDays(now, 30);
      
      let totalIncome = 0;
      let totalExpense = 0;
      let overdueAmount = 0;
      let pendingAmount = 0;
      let paidThisMonth = 0;
      let incomeThirtyDays = 0;
      
      const clientsWithOverdue = new Set();
      
      transactions.forEach(transaction => {
        if (transaction.type === 'income') {
          totalIncome += transaction.amount;
          
          if (transaction.date >= thirtyDaysAgo) {
            incomeThirtyDays += transaction.amount;
          }
        } else {
          totalExpense += transaction.amount;
        }
        
        if (transaction.status === 'paid' && 
            transaction.date >= thisMonthStart && 
            transaction.date <= thisMonthEnd) {
          paidThisMonth += transaction.amount;
        }
        
        if (transaction.type === 'income') {
          if (transaction.status === 'overdue') {
            overdueAmount += transaction.amount;
            if (transaction.client_id) {
              clientsWithOverdue.add(transaction.client_id);
            }
          } else if (transaction.status === 'pending') {
            pendingAmount += transaction.amount;
          }
        }
      });
      
      const balance = totalIncome - totalExpense;
      const averageIncome30Days = incomeThirtyDays / 30;
      
      return {
        totalIncome,
        totalExpense,
        balance,
        overdueAmount,
        pendingAmount,
        paidThisMonth,
        averageIncome30Days,
        clientsWithOverduePayments: clientsWithOverdue.size
      };
    },
    enabled: !isLoadingTransactions
  });
  
  const addTransactionMutation = useMutation({
    mutationFn: async (transaction: Omit<Transaction, "id">) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('financial_transactions')
        .insert({
          description: transaction.description,
          amount: transaction.amount,
          category: transaction.category,
          transaction_date: format(transaction.date, 'yyyy-MM-dd'),
          transaction_type: transaction.type,
          paid: transaction.status === 'paid',
          status: transaction.status,
          client_id: transaction.client_id,
          user_id: user.id
        })
        .select();
      
      if (error) throw error;
      return data[0];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['financial-summary'] });
      toast.success('Transação adicionada com sucesso!');
    },
    onError: (error: any) => {
      toast.error(`Erro ao adicionar transação: ${error.message}`);
    }
  });
  
  const updateTransactionStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string, status: "paid" | "pending" | "overdue" }) => {
      const { data, error } = await supabase
        .from('financial_transactions')
        .update({
          paid: status === 'paid',
          status: status
        })
        .eq('id', id)
        .select();
      
      if (error) throw error;
      return data[0];
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['financial-summary'] });
      toast.success('Status da transação atualizado com sucesso!');
    },
    onError: (error: any) => {
      toast.error(`Erro ao atualizar status: ${error.message}`);
    }
  });
  
  const deleteTransactionMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('financial_transactions')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      queryClient.invalidateQueries({ queryKey: ['financial-summary'] });
      toast.success('Transação excluída com sucesso!');
    },
    onError: (error: any) => {
      toast.error(`Erro ao excluir transação: ${error.message}`);
    }
  });
  
  const filteredTransactions = transactions.filter(transaction => {
    if (searchQuery && !transaction.description.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    
    if (filter === "all") return true;
    if (filter === "income") return transaction.type === "income";
    if (filter === "expense") return transaction.type === "expense";
    if (filter === "paid") return transaction.status === "paid";
    if (filter === "pending") return transaction.status === "pending";
    if (filter === "overdue") return transaction.status === "overdue";
    return true;
  });
  
  const kanbanGroups = {
    pending: filteredTransactions.filter(t => t.status === "pending"),
    paid: filteredTransactions.filter(t => t.status === "paid"),
    overdue: filteredTransactions.filter(t => t.status === "overdue")
  };
  
  const expensesByCategory = transactions
    .filter(t => t.type === "expense")
    .reduce((acc, t) => {
      acc[t.category] = (acc[t.category] || 0) + t.amount;
      return acc;
    }, {} as Record<string, number>);
    
  const incomeByCategory = transactions
    .filter(t => t.type === "income")
    .reduce((acc, t) => {
      acc[t.category] = (acc[t.category] || 0) + t.amount;
      return acc;
    }, {} as Record<string, number>);
  
  const handleAddTransaction = (transaction: Transaction) => {
    addTransactionMutation.mutate(transaction);
  };
  
  const handleStatusChange = (id: string, newStatus: "paid" | "pending" | "overdue") => {
    updateTransactionStatusMutation.mutate({ id, status: newStatus });
  };

  const handleDeleteTransaction = (id: string) => {
    return deleteTransactionMutation.mutateAsync(id);
  };
  
  const generateReport = () => {
    toast.success("Relatório gerado com sucesso!");
  };

  const pieChartData = Object.entries(expensesByCategory).map(([category, amount]) => ({
    name: category,
    value: amount
  }));

  const barChartData = [
    { name: 'Jan', Receitas: 4000, Despesas: 2400 },
    { name: 'Fev', Receitas: 3000, Despesas: 1398 },
    { name: 'Mar', Receitas: 2000, Despesas: 9800 },
    { name: 'Abr', Receitas: 2780, Despesas: 3908 },
    { name: 'Mai', Receitas: 1890, Despesas: 4800 },
    { name: 'Jun', Receitas: 2390, Despesas: 3800 }
  ];

  return (
    <AdminAccess>
      <div className="container mx-auto py-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold mb-2">Financeiro</h1>
            <p className="text-muted-foreground">
              Gerencie suas receitas, despesas e acompanhe seu fluxo de caixa
            </p>
          </div>
          <div className="flex gap-2 mt-4 sm:mt-0">
            <Button variant="outline" onClick={generateReport}>
              <FileText className="mr-2 h-4 w-4" />
              Relatório
            </Button>
            <RecurringTransactionDialog 
              clients={clients}
              onRefresh={() => {
                queryClient.invalidateQueries({ queryKey: ['recurring-transactions'] });
                generateTransactions();
              }}
              trigger={
                <Button variant="outline">
                  <CalendarClock className="mr-2 h-4 w-4" />
                  Cobrança Recorrente
                </Button>
              }
            />
            <TransactionDialog 
              onSave={handleAddTransaction}
              clients={clients}
              trigger={
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  Nova Transação
                </Button>
              }
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Receitas Totais
              </CardTitle>
              <ArrowUp className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-500">
                R$ {financialSummary.totalIncome.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </div>
              <p className="text-xs text-muted-foreground">
                Média diária: R$ {financialSummary.averageIncome30Days.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Despesas Totais
              </CardTitle>
              <ArrowDown className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-500">
                R$ {financialSummary.totalExpense.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </div>
              <p className="text-xs text-muted-foreground">
                Pagamentos deste mês: R$ {financialSummary.paidThisMonth.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Saldo
              </CardTitle>
              <DollarSign className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${financialSummary.balance >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                R$ {financialSummary.balance.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </div>
              <p className="text-xs text-muted-foreground">
                Atualizado em {format(new Date(), "dd 'de' MMMM", { locale: ptBR })}
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                A Receber
              </CardTitle>
              <Clock className="h-4 w-4 text-amber-500" />
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold text-amber-500">
                R$ {financialSummary.pendingAmount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </div>
              <p className="text-xs text-muted-foreground">
                Pagamentos pendentes
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Em Atraso
              </CardTitle>
              <AlertCircle className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold text-red-500">
                R$ {financialSummary.overdueAmount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </div>
              <p className="text-xs text-muted-foreground">
                Pagamentos vencidos
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Clientes Inadimplentes
              </CardTitle>
              <UsersIcon className="h-4 w-4 text-red-500" />
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold text-red-500">
                {financialSummary.clientsWithOverduePayments}
              </div>
              <p className="text-xs text-muted-foreground">
                Clientes com pagamentos atrasados
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Próximos Pagamentos
              </CardTitle>
              <CalendarClock className="h-4 w-4 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-xl font-bold text-blue-500">
                {recurringTransactions.filter(t => t.days_until_due && t.days_until_due <= 7).length}
              </div>
              <p className="text-xs text-muted-foreground">
                Vencimentos nos próximos 7 dias
              </p>
            </CardContent>
          </Card>
        </div>
        
        <Tabs defaultValue="transactions" className="space-y-6">
          <TabsList>
            <TabsTrigger value="transactions">Transações</TabsTrigger>
            <TabsTrigger value="recurring">Cobranças Recorrentes</TabsTrigger>
            <TabsTrigger value="charts">Gráficos</TabsTrigger>
          </TabsList>
          
          <TabsContent value="transactions">
            <Card>
              <CardHeader>
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
                  <div>
                    <CardTitle>Transações</CardTitle>
                    <CardDescription>
                      Visualize todas as suas receitas e despesas
                    </CardDescription>
                  </div>
                  <div className="flex gap-2 mt-4 md:mt-0">
                    <Button 
                      variant={view === "list" ? "default" : "outline"} 
                      size="sm"
                      onClick={() => setView("list")}
                    >
                      <FileText className="h-4 w-4 mr-1" />
                      Lista
                    </Button>
                    <Button 
                      variant={view === "kanban" ? "default" : "outline"} 
                      size="sm"
                      onClick={() => setView("kanban")}
                    >
                      <BarChart4 className="h-4 w-4 mr-1" />
                      Kanban
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col md:flex-row items-center gap-4 mb-4">
                  <div className="relative w-full md:w-64">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar transações..."
                      className="pl-8"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  
                  <Select value={period} onValueChange={setPeriod}>
                    <SelectTrigger className="w-full md:w-[180px]">
                      <SelectValue placeholder="Período" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="week">Esta Semana</SelectItem>
                      <SelectItem value="month">Este Mês</SelectItem>
                      <SelectItem value="quarter">Este Trimestre</SelectItem>
                      <SelectItem value="year">Este Ano</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Select value={filter} onValueChange={setFilter}>
                    <SelectTrigger className="w-full md:w-[180px]">
                      <SelectValue placeholder="Filtro" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas Transações</SelectItem>
                      <SelectItem value="income">Apenas Receitas</SelectItem>
                      <SelectItem value="expense">Apenas Despesas</SelectItem>
                      <SelectItem value="paid">Pagas</SelectItem>
                      <SelectItem value="pending">Pendentes</SelectItem>
                      <SelectItem value="overdue">Atrasadas</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {isLoadingTransactions ? (
                  <div className="py-4 text-center">Carregando transações...</div>
                ) : (
                  view === "list" ? (
                    <TransactionList 
                      transactions={filteredTransactions}
                      clients={clients}
                      onStatusChange={handleStatusChange}
                      onDelete={handleDeleteTransaction}
                    />
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <Card className="border-amber-200 dark:border-amber-800">
                        <CardHeader className="pb-2">
                          <CardTitle className="flex items-center text-lg">
                            <Clock className="h-5 w-5 mr-2 text-amber-500" />
                            Pendentes
                            <Badge className="ml-2" variant="outline">{kanbanGroups.pending.length}</Badge>
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="max-h-[60vh] overflow-y-auto pt-0">
                          {kanbanGroups.pending.length > 0 ? (
                            <div className="space-y-3">
                              {kanbanGroups.pending.map(transaction => (
                                <Card key={transaction.id} className="border-amber-200 dark:border-amber-800">
                                  <CardContent className="p-3">
                                    <div className="flex justify-between items-start mb-2">
                                      <div>
                                        <h3 className="font-medium">{transaction.description}</h3>
                                        <p className="text-sm text-muted-foreground">{transaction.clientName}</p>
                                      </div>
                                      <Badge variant={transaction.type === "income" ? "default" : "destructive"}>
                                        {transaction.type === "income" ? "Receita" : "Despesa"}
                                      </Badge>
                                    </div>
                                    <div className="flex justify-between items-center">
                                      <p className={`font-medium ${transaction.type === "income" ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
                                        R$ {transaction.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                      </p>
                                      <p className="text-sm">{format(transaction.date, "dd/MM/yyyy")}</p>
                                    </div>
                                    <div className="mt-2 pt-2 border-t flex justify-between">
                                      <Button 
                                        size="sm" 
                                        variant="ghost" 
                                        className="text-sm h-8"
                                        onClick={() => handleStatusChange(transaction.id, "overdue")}
                                      >
                                        <AlertCircle className="h-3.5 w-3.5 mr-1" />
                                        Em atraso
                                      </Button>
                                      <Button 
                                        size="sm" 
                                        variant="outline" 
                                        className="text-sm h-8 text-green-600"
                                        onClick={() => handleStatusChange(transaction.id, "paid")}
                                      >
                                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                                        Confirmar
                                      </Button>
                                    </div>
                                  </CardContent>
                                </Card>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-4 text-muted-foreground">
                              Nenhuma transação pendente
                            </div>
                          )}
                        </CardContent>
                      </Card>
                      
                      <Card className="border-red-200 dark:border-red-800">
                        <CardHeader className="pb-2">
                          <CardTitle className="flex items-center text-lg">
                            <AlertCircle className="h-5 w-5 mr-2 text-red-500" />
                            Em Atraso
                            <Badge className="ml-2" variant="outline">{kanbanGroups.overdue.length}</Badge>
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="max-h-[60vh] overflow-y-auto pt-0">
                          {kanbanGroups.overdue.length > 0 ? (
                            <div className="space-y-3">
                              {kanbanGroups.overdue.map(transaction => (
                                <Card key={transaction.id} className="border-red-200 dark:border-red-800">
                                  <CardContent className="p-3">
                                    <div className="flex justify-between items-start mb-2">
                                      <div>
                                        <h3 className="font-medium">{transaction.description}</h3>
                                        <p className="text-sm text-muted-foreground">{transaction.clientName}</p>
                                      </div>
                                      <Badge variant={transaction.type === "income" ? "default" : "destructive"}>
                                        {transaction.type === "income" ? "Receita" : "Despesa"}
                                      </Badge>
                                    </div>
                                    <div className="flex justify-between items-center">
                                      <p className={`font-medium ${transaction.type === "income" ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
                                        R$ {transaction.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                      </p>
                                      <p className="text-sm text-red-500">{format(transaction.date, "dd/MM/yyyy")}</p>
                                    </div>
                                    <div className="mt-2 pt-2 border-t flex justify-between">
                                      <Button 
                                        size="sm" 
                                        variant="ghost" 
                                        className="text-sm h-8"
                                        onClick={() => handleStatusChange(transaction.id, "pending")}
                                      >
                                        <Clock className="h-3.5 w-3.5 mr-1" />
                                        Pendente
                                      </Button>
                                      <Button 
                                        size="sm" 
                                        variant="outline" 
                                        className="text-sm h-8 text-green-600"
                                        onClick={() => handleStatusChange(transaction.id, "paid")}
                                      >
                                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                                        Confirmar
                                      </Button>
                                    </div>
                                  </CardContent>
                                </Card>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-4 text-muted-foreground">
                              Nenhuma transação em atraso
                            </div>
                          )}
                        </CardContent>
                      </Card>
                      
                      <Card className="border-green-200 dark:border-green-800">
                        <CardHeader className="pb-2">
                          <CardTitle className="flex items-center text-lg">
                            <CheckCircle2 className="h-5 w-5 mr-2 text-green-500" />
                            Pagas
                            <Badge className="ml-2" variant="outline">{kanbanGroups.paid.length}</Badge>
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="max-h-[60vh] overflow-y-auto pt-0">
                          {kanbanGroups.paid.length > 0 ? (
                            <div className="space-y-3">
                              {kanbanGroups.paid.map(transaction => (
                                <Card key={transaction.id} className="border-green-200 dark:border-green-800">
                                  <CardContent className="p-3">
                                    <div className="flex justify-between items-start mb-2">
                                      <div>
                                        <h3 className="font-medium">{transaction.description}</h3>
                                        <p className="text-sm text-muted-foreground">{transaction.clientName}</p>
                                      </div>
                                      <Badge variant={transaction.type === "income" ? "default" : "destructive"}>
                                        {transaction.type === "income" ? "Receita" : "Despesa"}
                                      </Badge>
                                    </div>
                                    <div className="flex justify-between items-center">
                                      <p className={`font-medium ${transaction.type === "income" ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
                                        R$ {transaction.amount.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                                      </p>
                                      <p className="text-sm">{format(transaction.date, "dd/MM/yyyy")}</p>
                                    </div>
                                    <div className="mt-2 pt-2 border-t flex justify-end">
                                      <Button 
                                        size="sm" 
                                        variant="ghost" 
                                        className="text-sm h-8"
                                        onClick={() => handleStatusChange(transaction.id, "pending")}
                                      >
                                        <Clock className="h-3.5 w-3.5 mr-1" />
                                        Marcar como pendente
                                      </Button>
                                    </div>
                                  </CardContent>
                                </Card>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-4 text-muted-foreground">
                              Nenhuma transação paga
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  )
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="recurring">
            <Card>
              <CardHeader>
                <CardTitle>Cobranças Recorrentes</CardTitle>
                <CardDescription>
                  Gerencie pagamentos e recebimentos que acontecem regularmente
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingRecurring ? (
                  <div className="py-4 text-center">Carregando cobranças recorrentes...</div>
                ) : (
                  <RecurringTransactionList 
                    recurringTransactions={recurringTransactions}
                    onRefresh={() => {
                      queryClient.invalidateQueries({ queryKey: ['recurring-transactions'] });
                    }}
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="charts">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Receitas x Despesas</CardTitle>
                  <CardDescription>
                    Comparativo mensal de entradas e saídas
                  </CardDescription>
                </CardHeader>
                <CardContent className="h-[400px]">
                  <FinanceChart 
                    data={barChartData}
                    type="bar"
                    colors={['#22c55e', '#ef4444']}
                  />
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Despesas por Categoria</CardTitle>
                  <CardDescription>
                    Distribuição de gastos por categoria
                  </CardDescription>
                </CardHeader>
                <CardContent className="h-[400px]">
                  <FinanceChart 
                    data={pieChartData}
                    type="pie"
                    colors={['#3b82f6', '#8b5cf6', '#ec4899', '#f43f5e', '#f97316', '#eab308']}
                    expensesByCategory={expensesByCategory}
                  />
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </AdminAccess>
  );
}
